import { Box, Chip, Stack, Tooltip } from '@mui/material';

import {
  CONFIDENCE_BAND_ACCURACY_REFERENCE,
  CONFIDENCE_BAND_COLOR,
  deriveConfidenceBand
} from './confidence_band';

interface ConfidenceBandChipProps {
  /** System-computed confidence in [0, 1], or null when not computed. */
  confidence: number | null;
  /**
   * Whether the record has been approved. The score describes the original
   * extraction, so on an approved record the tooltip says so explicitly.
   */
  approved?: boolean;
  /**
   * Render as coloured text rather than a filled chip. Used where the score sits
   * UNDER another chip in the same cell (the grid's Trust column): two stacked
   * chips overflow the row height and read as two equally-weighted claims, when
   * the score is really supporting detail for the mapping above it. The band
   * colour, tooltip and accessible name are identical either way.
   */
  compact?: boolean;
}

/**
 * Coloured chip showing the system-computed confidence as a PERCENTAGE (the number
 * is the reviewer-facing language — Maxine, 2026-07-03); the band still drives the
 * colour (high = green, medium = amber, low = red) and moves to the tooltip, which
 * names it and adds the band-level historical-accuracy reference (how often this
 * band has matched the verified answer on the reference golden) — labelled as
 * confidence, not as a per-cell accuracy, since a single cell has no ground truth.
 * @param {number | null} confidence System-computed confidence in [0, 1], or null.
 * @returns {JSX.Element}
 */
export default function ConfidenceBandChip({
  confidence,
  approved = false,
  compact = false
}: ConfidenceBandChipProps) {
  if (confidence === null) {
    return compact ? (
      <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
        n/a
      </Box>
    ) : (
      <Chip label="n/a" size="small" variant="outlined" aria-label="Confidence not computed" />
    );
  }
  const band = deriveConfidenceBand(confidence);
  const percent = `${Math.round(confidence * 100)}%`;
  const tooltip = (
    <Stack spacing={0.5}>
      <span>{`${band.charAt(0).toUpperCase()}${band.slice(1)} confidence`}</span>
      <span>{CONFIDENCE_BAND_ACCURACY_REFERENCE[band]}</span>
      {/* A low score beside a green "Approved" chip reads as a contradiction
          (Maxine 2026-08-03 ⑩). It is not: the score is how the SYSTEM read the
          cell, and the reviewer has since overridden that judgement by approving. */}
      {approved && (
        <span>
          How the system read this cell at extraction — the reviewer has since approved it.
        </span>
      )}
    </Stack>
  );
  // The visible label stays number-led (Maxine), but the BAND must not be
  // colour-only: role="img" makes the aria-label a real accessible name (a bare
  // Chip is a roleless div, which assistive tech never announces).
  const accessibleName = `${percent} — ${band} confidence`;
  return (
    <Tooltip title={tooltip}>
      {compact ? (
        <Box
          component="span"
          role="img"
          aria-label={accessibleName}
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1.2,
            color: `${CONFIDENCE_BAND_COLOR[band]}.main`
          }}
        >
          {percent}
        </Box>
      ) : (
        <Chip
          label={percent}
          role="img"
          aria-label={accessibleName}
          size="small"
          color={CONFIDENCE_BAND_COLOR[band]}
        />
      )}
    </Tooltip>
  );
}
